import React, { useRef, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { onEnterKeyEvent } from 'utils/events'
import Item from './Item'
import Counter from './Counter'
import SlideButtons from './SlideButtons'
import { FadeBackground } from './style'

const CloseButton = styled(FadeBackground)`
  position: absolute;
  z-index: 10;
  top: 16px;
  right: 16px;
  cursor: pointer;
  user-select: none;
`

const Container = styled.div`
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: rgba(54, 52, 52, 0.9);
  z-index: 9101; // the TabBar has z-index 9100...
`

const Carousel = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  max-width: 90%;
  max-height: 90%;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  scroll-behavior: auto;
  background: transparent;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`

const ImageCarousel = ({ images, scrollToIndex = null, onClose = () => {} }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const carouselRef = useRef()
  const hasScrolled = useRef()

  const handleSlide = useCallback(
    (delta, event = null) => {
      event?.preventDefault()

      const { current } = carouselRef
      const width = current.offsetWidth

      if (activeImageIndex + delta > images.length) {
        current.scrollTo(0, 0)
        return
      } else if (activeImageIndex + delta < 1) {
        current.scrollTo(width * images.length - 1, 0)
        return
      }
      current.scrollTo(current.scrollLeft + width * delta, 0)
    },
    [activeImageIndex, images.length],
  )

  const onKeyDown = useCallback(
    (e) => {
      const { key, keyCode } = e

      if (key === 'Escape' || keyCode === 27) {
        onClose()
      } else if (key === 'ArrowRight' || keyCode === 39) {
        handleSlide(1, e)
      } else if (key === 'ArrowLeft' || keyCode === 37) {
        handleSlide(-1, e)
      }
    },
    [handleSlide, onClose],
  )

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onKeyDown])

  useEffect(() => {
    if (!hasScrolled.current && carouselRef.current && scrollToIndex) {
      handleSlide(scrollToIndex)
      hasScrolled.current = true
    }
  }, [handleSlide, scrollToIndex])

  if (!images.length) {
    return null
  }

  return (
    <Container data-testid="carousel">
      <CloseButton
        onKeyDown={(e) => onEnterKeyEvent(e, onClose)}
        onClick={onClose}
        aria-label="Stäng bildkarusell"
        role="button"
        tabIndex="0"
      >
        Stäng
      </CloseButton>
      <SlideButtons onButtonClick={handleSlide} />
      <Carousel ref={carouselRef}>
        {images.map((image, i) => (
          <Item
            key={i + image.src}
            src={image.src}
            thumbnail={image.thumbnail}
            alt={`Bild nummer ${i + 1}`}
            onImageIntersect={() => setActiveImageIndex(i + 1)}
          />
        ))}
        <Counter activeIndex={activeImageIndex} total={images.length} />
      </Carousel>
    </Container>
  )
}

ImageCarousel.propTypes = {
  images: PropTypes.array.isRequired,
  scrollToIndex: PropTypes.number,
  onClose: PropTypes.func,
}

export default ImageCarousel
